import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "../../utils/toast";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, Chip, Card, CardContent, CardActions, Box, Divider, Grid, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Tooltip } from "@material-ui/core";
import { Visibility, VisibilityOff, CloudUpload, Delete, Description, LocalOffer, ExpandMore, HelpOutline, GetApp } from "@material-ui/icons";
import { InputAdornment, IconButton, Typography } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import NumberField from "../../components/NumberField";
import QueueSelect from "../../components/QueueSelect";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanies from "../../hooks/useCompanies";

const TAG_SUGGESTIONS = ["FAQ", "Suporte", "Produtos", "Políticas", "Instrução", "Sistema", "Conhecimento", "Treinamento", "Atendimento"];

const useStyles = makeStyles(theme => ({
    root: { display: "flex", flexWrap: "wrap" },
    btnWrapper: { position: "relative" },
    buttonProgress: { color: green[500], position: "absolute", top: "50%", left: "50%", marginTop: -12, marginLeft: -12 },
    sectionTitle: { fontWeight: 600, fontSize: "0.95rem", color: theme.palette.primary.main, marginTop: theme.spacing(2), marginBottom: theme.spacing(1), display: "flex", alignItems: "center", gap: 8 },
    dropZone: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(3),
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        "&:hover": { borderColor: theme.palette.primary.main, background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", boxShadow: theme.shadows[4] },
    },
    dropZoneDrag: { borderColor: theme.palette.primary.main, background: theme.palette.type === "dark" ? "rgba(100,181,246,0.15)" : "rgba(25,118,210,0.12)", boxShadow: theme.shadows[6] },
    fileCard: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        transition: "box-shadow 0.2s ease, transform 0.15s ease",
        "&:hover": { boxShadow: theme.shadows[4], transform: "translateY(-2px)" },
    },
    fileIcon: { fontSize: 32, color: theme.palette.primary.main, marginRight: theme.spacing(1.5) },
    chip: { margin: 2, fontSize: "0.7rem", height: 24 },
    tagSection: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, marginTop: 6 },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(3, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().notRequired(),
    voice: Yup.string().required("Informe o modo para Voz"),
    maxTokens: Yup.number().required("Informe o número máximo de tokens"),
    provider: Yup.string().required("Selecione o provedor"),
    model: Yup.string().required("Selecione o modelo"),
    temperature: Yup.number().required("Informe a temperatura"),
    apiKey: Yup.string().required("Informe a API Key"),
    maxMessages: Yup.number().required("Informe o número máximo de mensagens"),
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);
    const [showFallbackApiKey, setShowFallbackApiKey] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [ragFiles, setRagFiles] = useState([]);
    const [savedRagFiles, setSavedRagFiles] = useState([]);
    const [pendingTags, setPendingTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const fileInputRef = useRef(null);
    const { user } = useContext(AuthContext);
    const { list: listCompanies } = useCompanies();

    const initialState = {
        name: "", prompt: "", provider: "openai", model: "gpt-3.5-turbo",
        knowledgeContext: "", systemContext: "", ragContext: "", instructionContext: "", userContext: "",
        voice: "texto", voiceKey: "", voiceRegion: "",
        maxTokens: 220, temperature: 0.3, apiKey: "", queueId: null, maxMessages: 6,
        isActive: true, agentName: "", agentPersonality: "", agentRole: "",
        maxAutoReplies: 15, typingDelay: true, bypassSchedule: false,
        queueIds: [], queueOptionIds: [],
        fallbackProvider: "", fallbackApiKey: "",
        specialty: "", tags: [],
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const defaultCompanyId = Number(user?.companyId || localStorage.getItem("companyId") || 0) || null;
        if (open) setSelectedCompanyId(defaultCompanyId);

        const loadCompanies = async () => {
            if (!open || !user?.super) return;
            try {
                const companyList = await listCompanies();
                setCompanies(Array.isArray(companyList) ? companyList : []);
            } catch (err) { toastError(err); }
        };
        loadCompanies();

        const fetchPrompt = async () => {
            if (!promptId) { setPrompt(initialState); setSavedRagFiles([]); return; }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                const queueIds = data.queues ? data.queues.map(q => q.id) : [];
                const queueOptionIds = Array.isArray(data.queueOptionIds) ? data.queueOptionIds : [];
                setPrompt(prev => ({ ...prev, ...data, queueIds, queueOptionIds }));
                setSelectedVoice(data.voice);
                setSelectedCompanyId(Number(data.companyId || defaultCompanyId || 0) || null);
                const { data: ragFilesData } = await api.get(`/prompt/${promptId}/rag-files`, {
                    params: { companyId: Number(data.companyId || defaultCompanyId || 0) }
                });
                setSavedRagFiles(ragFilesData);
            } catch (err) { toastError(err); }
        };
        fetchPrompt();
    }, [promptId, open, user?.companyId, user?.super]);

    const handleClose = () => { setPrompt(initialState); setSelectedVoice("texto"); setRagFiles([]); setPendingTags([]); setTagInput(""); setUploading(false); setPreviewOpen(false); setPreviewData(null); onClose(); };

    const handleDeleteRagFile = async (filename) => {
        try {
            await api.delete(`/prompt/${promptId}/rag-files/${filename}`, { params: { companyId: selectedCompanyId || user?.companyId } });
            setSavedRagFiles(prev => prev.filter(f => f.name !== filename));
            toast.success("Arquivo excluído");
        } catch (err) { toastError(err); }
    };

    const handleUpdateTags = async (filename, newTags) => {
        try {
            await api.put(`/prompt/${promptId}/rag-files/${filename}/tags`, { tags: newTags, companyId: selectedCompanyId || user?.companyId });
            setSavedRagFiles(prev => prev.map(f => f.name === filename ? { ...f, tags: newTags } : f));
        } catch (err) { toastError(err); }
    };

    const handleAddTagToFile = (filename, tag) => {
        const file = savedRagFiles.find(f => f.name === filename);
        if (!file || file.tags.includes(tag)) return;
        handleUpdateTags(filename, [...file.tags, tag]);
    };

    const handleRemoveTagFromFile = (filename, tag) => {
        const file = savedRagFiles.find(f => f.name === filename);
        if (!file) return;
        handleUpdateTags(filename, file.tags.filter(t => t !== tag));
    };

    const handleAddPendingTag = (tag) => {
        const t = tag.trim();
        if (t && !pendingTags.includes(t)) setPendingTags(prev => [...prev, t]);
        setTagInput("");
    };

    const handleDrop = (e) => {
        e.preventDefault(); setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files).filter(f => {
            const ext = f.name.split(".").pop()?.toLowerCase();
            return ["txt", "md", "csv", "json", "xml", "html", "yaml", "yml", "log"].includes(ext);
        });
        if (droppedFiles.length) setRagFiles(prev => [...prev, ...droppedFiles]);
    };

    const handleDownloadRagFile = async (filename) => {
        try {
            const companyId = selectedCompanyId || user?.companyId;
            const response = await api.get(`/prompt/${promptId}/rag-files/${encodeURIComponent(filename)}/download`, {
                params: { companyId },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toastError(err);
        }
    };

    const handlePreviewRagFile = async (filename) => {
        setPreviewLoading(true);
        setPreviewOpen(true);
        setPreviewData(null);
        try {
            const companyId = selectedCompanyId || user?.companyId;
            const { data } = await api.get(`/prompt/${promptId}/rag-files/${encodeURIComponent(filename)}/preview`, {
                params: { companyId },
            });
            setPreviewData(data);
        } catch (err) {
            toastError(err);
            setPreviewOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSavePrompt = async values => {
        const targetCompanyId = Number(selectedCompanyId || user?.companyId || localStorage.getItem("companyId") || 0);
        const promptData = { ...values, voice: selectedVoice, companyId: targetCompanyId };
        if (
            !values.queueId &&
            (!values.queueIds || values.queueIds.length === 0) &&
            (!values.queueOptionIds || values.queueOptionIds.length === 0)
        ) {
            toastError("Informe ao menos uma fila ou subfila");
            return;
        }
        try {
            let savedPrompt;
            if (promptId) {
                const { data } = await api.put(`/prompt/${promptId}`, promptData);
                savedPrompt = data;
            } else {
                const { data } = await api.post("/prompt", promptData);
                savedPrompt = data;
            }
            if (savedPrompt?.id && ragFiles.length > 0) {
                setUploading(true);
                const formData = new FormData();
                ragFiles.forEach(file => formData.append("files", file));
                formData.append("companyId", String(targetCompanyId));
                formData.append("tags", JSON.stringify(pendingTags));
                await api.post(`/prompt/${savedPrompt.id}/rag-upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 60000,
                });
                setUploading(false);
            }
            toast.success(i18n.t("promptModal.success"));
            handleClose();
        } catch (err) {
            setUploading(false);
            toastError(err);
        }
    };

    const voiceOptions = [
        { value: "texto", label: "Texto" },
        { value: "pt-BR-FranciscaNeural", label: "Francisca" },
        { value: "pt-BR-AntonioNeural", label: "Antônio" },
        { value: "pt-BR-BrendaNeural", label: "Brenda" },
        { value: "pt-BR-DonatoNeural", label: "Donato" },
        { value: "pt-BR-ElzaNeural", label: "Elza" },
        { value: "pt-BR-FabioNeural", label: "Fábio" },
        { value: "pt-BR-GiovannaNeural", label: "Giovanna" },
    ];

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="md" scroll="paper" fullWidth>
                <DialogTitle>{promptId ? i18n.t("promptModal.title.edit") : i18n.t("promptModal.title.add")}</DialogTitle>
                <Formik initialValues={prompt} enableReinitialize validationSchema={PromptSchema} onSubmit={(values, actions) => { setTimeout(() => { handleSavePrompt(values); actions.setSubmitting(false); }, 400); }}>
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                {/* ── Identificação ── */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Field as={TextField} label={i18n.t("promptModal.form.name")} name="name" error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name} variant="outlined" margin="dense" fullWidth />
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Empresa</InputLabel>
                                            <Select label="Empresa" value={selectedCompanyId || ""} onChange={e => setSelectedCompanyId(Number(e.target.value) || null)} disabled={!user?.super}>
                                                <MenuItem value="">Selecione</MenuItem>
                                                {user?.super ? companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>) : <MenuItem value={user?.companyId}>{user?.company?.name || `Empresa ${user?.companyId}`}</MenuItem>}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel error={touched.provider && Boolean(errors.provider)}>{i18n.t("promptModal.form.provider")}</InputLabel>
                                            <Field as={Select} name="provider" label={i18n.t("promptModal.form.provider")} error={touched.provider && Boolean(errors.provider)}>
                                                <MenuItem value="openai">OpenAI</MenuItem>
                                                <MenuItem value="groq">Groq</MenuItem>
                                                <MenuItem value="gemini">Gemini</MenuItem>
                                            </Field>
                                            {touched.provider && errors.provider && <Typography variant="caption" color="error">{errors.provider}</Typography>}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel error={touched.model && Boolean(errors.model)}>{i18n.t("promptModal.form.model")}</InputLabel>
                                            <Field as={Select} name="model" label={i18n.t("promptModal.form.model")} error={touched.model && Boolean(errors.model)}>
                                                {values.provider === "openai" && [
                                                    <MenuItem key="gpt-4o-mini" value="gpt-4o-mini">GPT-4o Mini</MenuItem>,
                                                    <MenuItem key="gpt-3.5-turbo-1106" value="gpt-3.5-turbo-1106">GPT-3.5 Turbo</MenuItem>,
                                                    <MenuItem key="gpt-4o" value="gpt-4o">GPT-4o</MenuItem>
                                                ]}
                                                {values.provider === "groq" && [
                                                    <MenuItem key="openai/gpt-oss-120b" value="openai/gpt-oss-120b">GPT-OSS (120B)</MenuItem>,
                                                    <MenuItem key="openai/gpt-oss-20b" value="openai/gpt-oss-20b">GPT-OSS (20B)</MenuItem>,
                                                    <MenuItem key="qwen/qwen3.6-27b" value="qwen/qwen3.6-27b">Qwen 3.6 (27B)</MenuItem>
                                                ]}
                                                {values.provider === "gemini" && [
                                                    <MenuItem key="gemini-1.5-pro" value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>,
                                                    <MenuItem key="gemini-1.5-flash" value="gemini-1.5-flash">Gemini 1.5 Flash</MenuItem>,
                                                    <MenuItem key="gemini-1.5-flash-8b" value="gemini-1.5-flash-8b">Gemini 1.5 Flash (8B)</MenuItem>
                                                ]}
                                            </Field>
                                            {touched.model && errors.model && <Typography variant="caption" color="error">{errors.model}</Typography>}
                                        </FormControl>
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <Field as={TextField} label={i18n.t("promptModal.form.apiKey")} name="apiKey" type={showApiKey ? "text" : "password"} variant="outlined" margin="dense" fullWidth
                                                error={touched.apiKey && Boolean(errors.apiKey)} helperText={touched.apiKey && errors.apiKey}
                                                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowApiKey(!showApiKey)}>{showApiKey ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box mt={1} mb={2}>
                                    <Field as={TextField} label={i18n.t("promptModal.form.prompt")} name="prompt" variant="outlined" margin="dense" fullWidth rows={6} multiline
                                        error={touched.prompt && Boolean(errors.prompt)} helperText={touched.prompt && errors.prompt} />
                                </Box>

                                {/* ── Perfil do Agente ── */}
                                <Divider style={{ margin: "16px 0 8px" }} />
                                <Typography className={classes.sectionTitle}>
                                    <span role="img" aria-label="Perfil do Agente">🧑‍💼</span> Perfil do Agente
                                    <Tooltip title="Entenda como configurar a Especialidade e Tags">
                                        <IconButton size="small" onClick={() => setInfoOpen(true)}>
                                            <HelpOutline fontSize="small" color="primary" />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                                    Configure a personalidade e especialização do agente para um atendimento mais humano.
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Field as={TextField} label="Nome do Agente" name="agentName" placeholder="Ex: Ana, Carlos, Sofia" variant="outlined" margin="dense" fullWidth />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field as={TextField} label="Função (Display)" name="agentRole" placeholder="Ex: Suporte Técnico, Vendas" variant="outlined" margin="dense" fullWidth />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Especialidade de Roteamento</InputLabel>
                                            <Field as={Select} name="specialty" label="Especialidade de Roteamento">
                                                <MenuItem value="">Nenhuma</MenuItem>
                                                <MenuItem value="TRIAGEM">Agente Master (Triagem)</MenuItem>
                                                <MenuItem value="PROSIPE_V5">Prosipe V5</MenuItem>
                                                <MenuItem value="PROSIPE_V2">Prosipe V2</MenuItem>
                                                <MenuItem value="PROSIPE_CHAMADOS">Prosipe Chamados</MenuItem>
                                                <MenuItem value="PROSIPE_EAD">Prosipe EAD</MenuItem>
                                                <MenuItem value="PROSIPE_LEGIS">Prosipe Legis</MenuItem>
                                                <MenuItem value="PROSIPE_SITES">Desenvolvimento de Sites</MenuItem>
                                                <MenuItem value="PROSIPE_PONTO">Prosipe Ponto</MenuItem>
                                                <MenuItem value="FINANCEIRO">Financeiro</MenuItem>
                                            </Field>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field name="tags">
                                            {({ field, form }) => (
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    options={[]}
                                                    value={field.value || []}
                                                    onChange={(_, newValue) => form.setFieldValue("tags", newValue)}
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => (
                                                            <Chip variant="outlined" label={option} {...getTagProps({ index })} color="primary" size="small" />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField {...params} variant="outlined" label="Tags Semânticas" margin="dense" placeholder="+Tag" fullWidth />
                                                    )}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field as={TextField} label="Personalidade" name="agentPersonality" placeholder="Descreva como o agente deve se comportar. Ex: Simpática, usa linguagem informal..." variant="outlined" margin="dense" fullWidth rows={3} multiline />
                                    </Grid>
                                </Grid>

                                {/* ── Configurações do Agente ── */}
                                <Divider style={{ margin: "16px 0 8px" }} />
                                <Typography className={classes.sectionTitle}>⚙️ Configurações do Agente</Typography>

                                <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
                                    <Grid item xs={12} sm={3}>
                                        <Field name="isActive">
                                            {({ field, form }) => (
                                                <FormControlLabel
                                                    control={<Switch color="primary" checked={field.value} onChange={e => form.setFieldValue("isActive", e.target.checked)} />}
                                                    label={<Typography variant="body2">Agente Ativo</Typography>}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field name="typingDelay">
                                            {({ field, form }) => (
                                                <FormControlLabel
                                                    control={<Switch color="primary" checked={field.value} onChange={e => form.setFieldValue("typingDelay", e.target.checked)} />}
                                                    label={<Typography variant="body2">Simular Digitação</Typography>}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field name="bypassSchedule">
                                            {({ field, form }) => (
                                                <FormControlLabel
                                                    control={<Switch color="primary" checked={field.value} onChange={e => form.setFieldValue("bypassSchedule", e.target.checked)} />}
                                                    label={<Typography variant="body2">Ignorar Expediente</Typography>}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field as={NumberField} label="Máx. Respostas" name="maxAutoReplies" fullWidth helperText="0 = ilimitado" />
                                    </Grid>
                                </Grid>

                                <div style={{ marginTop: 8 }}>
                                    <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: "block" }}>Filas Atendidas (múltiplas)</Typography>
                                    <Field name="queueIds">
                                        {({ field, form }) => (
                                            <QueueSelect
                                                selectedQueueIds={field.value || []}
                                                selectedQueueOptionIds={values.queueOptionIds || []}
                                                onChange={(selectedIds, selectedOptionIds) => {
                                                    form.setFieldValue("queueIds", selectedIds);
                                                    form.setFieldValue("queueOptionIds", selectedOptionIds);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </div>

                                {/* ── Base de Conhecimento RAG ── */}
                                <Divider style={{ margin: "16px 0 8px" }} />
                                <Typography className={classes.sectionTitle}><Description /> Base de Conhecimento RAG</Typography>
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                                    Envie arquivos .txt com o conteúdo que a IA deve consultar. Use tags para categorizar cada documento.
                                </Typography>

                                {/* Saved files as cards */}
                                {savedRagFiles.length > 0 && (
                                    <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                        {savedRagFiles.map((file, idx) => (
                                            <Grid item xs={12} sm={6} key={idx}>
                                                <Card className={classes.fileCard} variant="outlined">
                                                    <CardContent style={{ paddingBottom: 4 }}>
                                                        <Box display="flex" alignItems="center">
                                                            <Description className={classes.fileIcon} />
                                                            <Box flex={1} minWidth={0}>
                                                                <Typography variant="body2" noWrap style={{ fontWeight: 600, cursor: "pointer" }} onClick={() => handlePreviewRagFile(file.name)}>{file.name}</Typography>
                                                                <Typography variant="caption" color="textSecondary">Salvo no bucket</Typography>
                                                            </Box>
                                                        </Box>
                                                        <div className={classes.tagSection}>
                                                            <LocalOffer style={{ fontSize: 14, opacity: 0.5 }} />
                                                            {file.tags?.map((tag, ti) => (
                                                                <Chip key={ti} label={tag} size="small" className={classes.chip} color="primary" variant="outlined"
                                                                    onDelete={() => handleRemoveTagFromFile(file.name, tag)} />
                                                            ))}
                                                            <Autocomplete
                                                                freeSolo
                                                                size="small"
                                                                options={TAG_SUGGESTIONS.filter(t => !file.tags?.includes(t))}
                                                                onChange={(e, newValue) => { if (newValue) handleAddTagToFile(file.name, newValue); }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && e.target.value) {
                                                                        e.preventDefault();
                                                                        handleAddTagToFile(file.name, e.target.value);
                                                                        e.target.value = "";
                                                                    }
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} variant="standard" placeholder="+ Tag" 
                                                                        InputProps={{ ...params.InputProps, disableUnderline: true, style: { fontSize: "0.75rem", padding: 0 } }} 
                                                                        style={{ minWidth: 100, marginLeft: 8 }} />
                                                                )}
                                                            />
                                                        </div>
                                                    </CardContent>
                                                    <CardActions style={{ justifyContent: "flex-end", paddingTop: 0 }}>
                                                        <Tooltip title="Visualizar conteúdo">
                                                            <IconButton size="small" onClick={() => handlePreviewRagFile(file.name)}><Visibility fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Baixar arquivo">
                                                            <IconButton size="small" onClick={() => handleDownloadRagFile(file.name)}><GetApp fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Excluir">
                                                            <IconButton size="small" onClick={() => handleDeleteRagFile(file.name)} style={{ color: "#f44336" }}><Delete fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {/* Pending files to upload */}
                                {ragFiles.length > 0 && (
                                    <Box mb={2}>
                                        <Typography variant="caption" style={{ fontWeight: 600 }}>Arquivos pendentes para upload:</Typography>
                                        <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginTop: 6 }}>
                                            {ragFiles.map((file, idx) => (
                                                <Chip key={idx} label={file.name} variant="outlined" color="default" size="small"
                                                    onDelete={() => setRagFiles(prev => prev.filter((_, i) => i !== idx))} style={{ borderStyle: "dashed" }} />
                                            ))}
                                        </Box>
                                        <Box mt={1} display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 6 }}>
                                            <Typography variant="caption" color="textSecondary">Tags para estes arquivos:</Typography>
                                            {pendingTags.map((tag, i) => (
                                                <Chip key={i} label={tag} size="small" color="primary" className={classes.chip}
                                                    onDelete={() => setPendingTags(prev => prev.filter((_, ti) => ti !== i))} />
                                            ))}
                                            <TextField size="small" placeholder="Adicionar tag..." value={tagInput}
                                                onChange={e => setTagInput(e.target.value)}
                                                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAddPendingTag(tagInput); } }}
                                                style={{ width: 130 }} InputProps={{ style: { fontSize: "0.8rem", height: 28 } }} />
                                            {TAG_SUGGESTIONS.filter(t => !pendingTags.includes(t)).slice(0, 4).map(t => (
                                                <Chip key={t} label={t} size="small" variant="outlined" className={classes.chip}
                                                    onClick={() => handleAddPendingTag(t)} style={{ cursor: "pointer", opacity: 0.6 }} />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {/* Drop zone */}
                                <div className={`${classes.dropZone} ${isDragging ? classes.dropZoneDrag : ""}`}
                                    onClick={() => !uploading && fileInputRef.current?.click()}
                                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onDrop={handleDrop}
                                    style={uploading ? { pointerEvents: "none", opacity: 0.5 } : {}}>
                                    {uploading ? (
                                        <>
                                            <CircularProgress size={36} style={{ marginBottom: 8 }} />
                                            <Typography variant="body2" color="textSecondary">Enviando arquivos...</Typography>
                                        </>
                                    ) : (
                                        <>
                                            <CloudUpload style={{ fontSize: 40, opacity: 0.4, marginBottom: 8 }} />
                                            <Typography variant="body2" color="textSecondary">
                                                Arraste arquivos <strong>.txt</strong> aqui ou clique para selecionar
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Formatos aceitos: .txt, .md, .csv, .json, .xml, .yaml
                                            </Typography>
                                        </>
                                    )}
                                    <input ref={fileInputRef} type="file" multiple hidden accept=".txt,.md,.csv,.json,.xml,.html,.yaml,.yml,.log"
                                        onChange={e => { const selected = Array.from(e?.target?.files || []); if (selected.length) setRagFiles(prev => [...prev, ...selected]); e.target.value = ""; }} />
                                </div>

                                {/* ── Configurações Avançadas ── */}
                                <Accordion style={{ marginTop: 24 }} variant="outlined">
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography className={classes.sectionTitle} style={{ margin: 0 }}>⚙️ Configurações Avançadas</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                        
                                        <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 8 }}>IA Secundária (Fallback)</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>IA Secundária</InputLabel>
                                                    <Field as={Select} name="fallbackProvider" label="IA Secundária">
                                                        <MenuItem value="">Nenhum</MenuItem>
                                                        <MenuItem value="openai">OpenAI</MenuItem>
                                                        <MenuItem value="groq">Groq</MenuItem>
                                                        <MenuItem value="gemini">Gemini</MenuItem>
                                                    </Field>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <Field name="fallbackProvider">
                                                    {({ field }) => field.value ? (
                                                        <Field as={TextField}
                                                            label="Chave API Secundária"
                                                            name="fallbackApiKey"
                                                            type={showFallbackApiKey ? "text" : "password"}
                                                            variant="outlined" margin="dense" fullWidth
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={() => setShowFallbackApiKey(!showFallbackApiKey)}>
                                                                            {showFallbackApiKey ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                )
                                                            }}
                                                        />
                                                    ) : <Box height={40} />}
                                                </Field>
                                            </Grid>
                                        </Grid>

                                        <Divider />
                                        <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 8 }}>Voz</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth margin="dense" variant="outlined">
                                                    <InputLabel>{i18n.t("promptModal.form.voice")}</InputLabel>
                                                    <Select label={i18n.t("promptModal.form.voice")} name="voice" value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)}>
                                                        {voiceOptions.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Field as={TextField} label={i18n.t("promptModal.form.voiceKey")} name="voiceKey" variant="outlined" margin="dense" fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Field as={TextField} label={i18n.t("promptModal.form.voiceRegion")} name="voiceRegion" variant="outlined" margin="dense" fullWidth />
                                            </Grid>
                                        </Grid>

                                        <Divider />
                                        <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: 8 }}>Geração</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Field as={NumberField} label={i18n.t("promptModal.form.temperature")} name="temperature" step={0.1} min={0} max={2} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Field as={NumberField} label={i18n.t("promptModal.form.maxTokens")} name="maxTokens" step={1} min={1} fullWidth />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Field as={NumberField} label={i18n.t("promptModal.form.maxMessages")} name="maxMessages" step={1} min={1} max={100} fullWidth />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose} color="secondary" disabled={isSubmitting || uploading} variant="outlined">{i18n.t("promptModal.buttons.cancel")}</Button>
                                <Button type="submit" color="primary" disabled={isSubmitting || uploading} variant="contained" className={classes.btnWrapper}>
                                    {uploading ? "Enviando..." : (promptId ? i18n.t("promptModal.buttons.okEdit") : i18n.t("promptModal.buttons.okAdd"))}
                                    {(isSubmitting || uploading) && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            {/* ── Modal Explicativo: Perfil do Agente ── */}
            <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Como configurar o Perfil do Agente?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
                        1. Especialidade de Roteamento (Chave Estrita)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Este é o motor do sistema de multi-agentes. É o código exato que a IA usará para transferir o atendimento.
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 4 }}>
                        <strong>Exemplo de uso:</strong> Se este agente for o Especialista do V5, selecione <em>PROSIPE_V5</em>. Se for o bot principal de atendimento, selecione <em>Agente Master (Triagem)</em>.
                    </Typography>
                    <Typography variant="caption" color="error" style={{ display: "block", marginBottom: 16 }}>
                        Atenção: O sistema usa essa chave para isolar o histórico e buscar a IA correta.
                    </Typography>

                    <Divider style={{ margin: "8px 0 12px" }} />

                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
                        2. Tags Semânticas (Contexto Livre)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Palavras-chave que ajudam o sistema a classificar os arquivos de conhecimento (RAG) deste agente.
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 16 }}>
                        <strong>Exemplo de uso:</strong> Digite <em>FAQ</em>, <em>Suporte Técnico</em>, <em>RH</em> e pressione Enter. Os arquivos enviados herdarão essas tags para a busca semântica da IA.
                    </Typography>

                    <Divider style={{ margin: "8px 0 12px" }} />

                    <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 4 }}>
                        3. Função (Display)
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Apenas o nome do cargo que a IA vai assumir na sua persona.
                    </Typography>
                    <Typography variant="body2">
                        <strong>Exemplo:</strong> <em>Suporte Financeiro</em> ou <em>Especialista em Ponto</em>.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoOpen(false)} color="primary" variant="contained">
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Modal Preview do arquivo RAG ── */}
            <Dialog open={previewOpen} onClose={() => { setPreviewOpen(false); setPreviewData(null); }} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Description color="primary" />
                            <span>{previewData?.name || "Carregando..."}</span>
                        </Box>
                        {previewData && (
                            <Box>
                                <Tooltip title="Baixar">
                                    <IconButton size="small" onClick={() => handleDownloadRagFile(previewData.name)}>
                                        <GetApp />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : previewData ? (
                        <>
                            <pre style={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                fontSize: "0.82rem",
                                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                                lineHeight: 1.6,
                                padding: 12,
                                borderRadius: 8,
                                maxHeight: "60vh",
                                overflow: "auto",
                            }}>
                                {previewData.content}
                            </pre>
                            {previewData.truncated && (
                                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8, textAlign: "center" }}>
                                    ⚠️ Mostrando os primeiros 10.000 caracteres de {previewData.totalLength.toLocaleString()} total. Baixe o arquivo para ver o conteúdo completo.
                                </Typography>
                            )}
                        </>
                    ) : (
                        <Typography color="textSecondary">Nenhum conteúdo disponível.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPreviewOpen(false); setPreviewData(null); }} color="primary" variant="contained">Fechar</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PromptModal;
